import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Card, CardContent, Divider, Grid, Typography } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useSetRecoilState, useRecoilState } from 'recoil';

import { getAreaReservationListApi } from '@apis/index';
import { MessageDialogState } from '@atom/DialogState';
import { LoadingState } from '@atom/LoadingState';

import {
  GetAreaReservationListQueryVariables,
  areaReservationListType,
  getAreaReservationListType
} from '../../../API';
import { GraphQLErrorResponse } from '../commonType';

const MonthlyScheduler = () => {
  /* 共通State */
  const setMessageDialogState = useSetRecoilState(MessageDialogState);
  const [loading, setLoading] = useRecoilState(LoadingState);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [displayData, setDisplayData] = useState<getAreaReservationListType>({
    __typename: 'getAreaReservationListType',
    areaReservationList: [],
    nextToken: ''
  });
  const areaId = searchParams.get('areaId') ?? '';
  const facilityId = searchParams.get('facilityId') ?? '';
  const title = searchParams.get('title') ?? '';
  const [selectedDate, setSelectedDate] = React.useState<Dayjs>(dayjs());

  const getData = async (isNewSearch = false) => {
    if (loading) {
      return;
    }
    if (isNewSearch) {
      setDisplayData({
        __typename: 'getAreaReservationListType',
        areaReservationList: [],
        nextToken: ''
      });
      return;
    }

    setLoading(true);
    try {
      const input: GetAreaReservationListQueryVariables = {
        input: {
          areaId,
          facilityId,
          targetDate: selectedDate.format('YYYY-MM-DD'),
          number: 50,
          nextToken: displayData.nextToken
        }
      };
      const result = await getAreaReservationListApi(input);

      setDisplayData((prevData) => {
        const dataMap = new Map<string, areaReservationListType>(
          prevData.areaReservationList?.map((item) => [
            `${item.date}-${item.reservedStartTime}-${item.reservedEndTime}-${item.sectionId ?? 'null'}`,
            item
          ]) || []
        );

        // 新しいデータをループし、同じ複合キーがあれば上書き、なければ追加
        result.areaReservationList?.forEach((newItem) => {
          const key = `${newItem.date}-${newItem.reservedStartTime}-${newItem.reservedEndTime}-${newItem.sectionId ?? 'null'}`;
          dataMap.set(key, newItem);
        });

        return {
          __typename: 'getAreaReservationListType',
          areaReservationList: Array.from(dataMap.values()),
          nextToken: result.nextToken
        };
      });
    } catch (error) {
      console.error(error);

      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if ('errors' in (error as GraphQLErrorResponse) && (error as GraphQLErrorResponse).errors.length !== 0) {
        message = (error as GraphQLErrorResponse).errors[0].message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // スクロール用関数
  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      getData();
    }
  };

  const selectColor = (sectionid: string) => {
    const id = sectionid?.slice(-1); // 区画IDの末尾
    let fontColor;
    switch (id) {
      case '1':
        fontColor = '#43CCCA';
        break;
      case '2':
        fontColor = '#BFE7F1';
        break;
      case '3':
        fontColor = '#ED86D3';
        break;
      case '4':
        fontColor = '#F69CC4';
        break;
      case '5':
        fontColor = '#F1A0A3';
        break;
      case '6':
        fontColor = '#FAC6CD';
        break;
      case '7':
        fontColor = '#EA88C1';
        break;
      case '8':
        fontColor = '#CE98DA';
        break;
      default:
        // 休館日想定
        fontColor = '#EEF1F4';
        break;
    }
    return fontColor;
  };

  const dayColor = (day: string) => {
    let fontColor;
    switch (day) {
      case '土':
        fontColor = '#5DC1F2';
        break;
      case '日':
        fontColor = '#EF556D';
        break;
      default:
        break;
    }
    return {
      fontSize: '20px',
      color: fontColor
    };
  };

  // トークンがリセットされた後にデータを取得
  useEffect(() => {
    if (facilityId && displayData.nextToken === '') {
      getData();
    }
  }, [displayData.nextToken, facilityId]);

  // 日付が変更された時、データをリセットして新しい検索をする
  useEffect(() => {
    if (facilityId) {
      getData(true);
    }
  }, [selectedDate, facilityId]);
  return (
    <Box ml={1}>
      <Grid
        container
        sx={{
          justifyContent: 'start',
          alignItems: 'start',
          width: '100%'
        }}
        spacing={1}
      >
        <Grid item md={2} xs={12}>
          <DateCalendar
            sx={{ width: '100%' }}
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
          />
        </Grid>
        <Grid item md={10} xs={12}>
          <Grid
            container
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              overflowY: 'scroll',
              height: '90vh'
            }}
            spacing={1}
            onScroll={handleScroll}
          >
            {displayData?.areaReservationList &&
              displayData.areaReservationList.length > 0 &&
              displayData.areaReservationList.map((item, index) => {
                const isDifferentDate = index === 0 || item.date !== displayData.areaReservationList?.[index - 1]?.date;
                const shouldDisplayCard = item.reservationFlag !== 0;

                return (
                  <>
                    {isDifferentDate && (
                      <Grid
                        item
                        xs={12}
                        sx={{ width: '100%', mt: 2 }}
                        onClick={() =>
                          navigate(
                            `/c006/sectionschedule/?facilityId=${facilityId}&areaId=${areaId}&date=${dayjs(item.date).format('YYYY-MM-DD')}&title=${title}`
                          )
                        }
                      >
                        <Divider textAlign="left" sx={{ width: '100%' }}>
                          {dayjs(item.date).format('D') === '1' ? dayjs(item.date).format('YYYY年MM月') : ''}
                        </Divider>
                        <Typography style={dayColor(dayjs(item.date).format('dd'))}>
                          {dayjs(item.date).format('D日(dd)')}
                        </Typography>
                      </Grid>
                    )}
                    {shouldDisplayCard && (
                      <Grid
                        item
                        xs={12}
                        sx={{ width: '100%', ml: 15 }}
                        onClick={() =>
                          navigate(
                            `/c006/sectionschedule/?facilityId=${facilityId}&areaId=${areaId}&date=${dayjs(item.date).format('YYYY-MM-DD')}&title=${title}`
                          )
                        }
                      >
                        <Card sx={{ padding: 2, backgroundColor: selectColor(item.sectionId ?? '') }}>
                          {item.reservationFlag === 2 ? '休館日' : item.sectionName} -{' '}
                          {item.reservedStartTime?.slice(0, -3)}〜{item.reservedEndTime?.slice(0, -3)}{' '}
                          {item.usagePurpose}
                        </Card>
                      </Grid>
                    )}
                  </>
                );
              })}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
export default MonthlyScheduler;
